/* ============================================================================
 * LIG — Personalize Your Design (/pages/personalized-gear)
 * Vanilla JS, no libraries, no build step, no CDN. Progressive enhancement:
 * every section renders complete without this file.
 *
 *   1. Scroll reveals        [data-pg-reveal] via IntersectionObserver
 *   2. Smooth anchor CTAs    a[data-pg-scroll]
 *   3. Story scrub           [data-pg-story] sticky stage, --p 0..1
 *   4. Before/after sliders  [data-pg-compare] pointer + keyboard
 *   5. Order intake          [data-pg-order] photo uploads (middleware) +
 *                            AJAX /cart/add.js with line-item properties,
 *                            then Horizon cart:update + drawer.open()
 *
 * Cart pipeline mirrors the proven lig-bundle-builder / lig-pair-feature flow
 * (explicit JSON POST -> refetch /cart.js -> dispatch a bubbling `cart:update`
 * event whose shape matches @theme/events CartAddEvent -> open the drawer;
 * CartAddEvent morphs the drawer contents but never opens it on its own).
 * ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function prefersReduced() {
    return !!(REDUCED && REDUCED.matches);
  }

  /* Theme globals (snippets/scripts.liquid declares `const Theme` at top level —
     a global lexical binding, not window.Theme — so probe with typeof). */
  function themeRoutes() {
    try {
      /* eslint-disable no-undef */
      if (typeof Theme !== 'undefined' && Theme && Theme.routes) return Theme.routes;
      /* eslint-enable no-undef */
    } catch (e) { /* noop */ }
    return {};
  }

  /* ------------------------------------------------------------------ *
   * 1. Scroll reveals
   * ------------------------------------------------------------------ */
  var revealObserver = null;

  function initReveals(scope) {
    var nodes = (scope || document).querySelectorAll('.lig-pg [data-pg-reveal]:not(.is-in)');
    if (!nodes.length) return;
    if (prefersReduced() || !('IntersectionObserver' in window)) {
      nodes.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }
    nodes.forEach(function (el) { revealObserver.observe(el); });
  }

  /* ------------------------------------------------------------------ *
   * 2. Smooth-scroll CTAs (native anchors still work without JS)
   * ------------------------------------------------------------------ */
  function initAnchors() {
    if (document.documentElement.dataset.pgAnchors === '1') return;
    document.documentElement.dataset.pgAnchors = '1';
    document.addEventListener('click', function (e) {
      var link = e.target && e.target.closest ? e.target.closest('a[data-pg-scroll]') : null;
      if (!link) return;
      var href = link.getAttribute('href') || '';
      if (href.charAt(0) !== '#') return;
      var target = document.querySelector(href);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: prefersReduced() ? 'auto' : 'smooth', block: 'start' });
    });
  }

  /* ------------------------------------------------------------------ *
   * 3. Story scrub — sticky stage, scroll progress drives --p (0..1)
   * ------------------------------------------------------------------ */
  function initStory(el) {
    if (el.dataset.pgReady === '1') return;
    el.dataset.pgReady = '1';
    if (prefersReduced()) return; // stays a static 50/50 split

    var stage = el.querySelector('[data-pg-stage]');
    if (!stage) return;

    el.classList.add('is-js'); // unlocks the tall scroll track + sticky stage

    var ticking = false;

    function measure() {
      var rect = el.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var track = rect.height - vh;
      var p = track > 0 ? Math.min(1, Math.max(0, -rect.top / track)) : 1;
      stage.style.setProperty('--p', p.toFixed(4));
      stage.classList.toggle('is-started', p > 0.04);
      stage.classList.toggle('is-after', p > 0.55);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        measure();
      });
    }

    // Only listen while the tall section is anywhere near the viewport.
    if ('IntersectionObserver' in window) {
      var bound = false;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !bound) {
            bound = true;
            window.addEventListener('scroll', onScroll, { passive: true });
            window.addEventListener('resize', onScroll);
            measure();
          } else if (!entry.isIntersecting && bound) {
            bound = false;
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
          }
        });
      }, { rootMargin: '25% 0px 25% 0px' });
      io.observe(el);
    } else {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
    }
    measure();
  }

  /* ------------------------------------------------------------------ *
   * 4. Before/after compare sliders — pointer, touch, keyboard
   * ------------------------------------------------------------------ */
  function initCompare(el) {
    if (el.dataset.pgReady === '1') return;
    el.dataset.pgReady = '1';

    var handle = el.querySelector('[data-pg-handle]');
    var start = parseFloat(el.dataset.pgStart);
    if (isNaN(start)) start = 50;
    var split = start;

    function setSplit(pct) {
      split = Math.max(0, Math.min(100, pct));
      el.style.setProperty('--split', split + '%');
      if (handle) {
        var rounded = Math.round(split);
        handle.setAttribute('aria-valuenow', String(rounded));
        handle.setAttribute('aria-valuetext', rounded + '% photo, ' + (100 - rounded) + '% design');
      }
    }

    function splitFromPointer(e) {
      var rect = el.getBoundingClientRect();
      if (!rect.width) return;
      setSplit(((e.clientX - rect.left) / rect.width) * 100);
    }

    el.addEventListener('pointerdown', function (e) {
      if (e.button !== undefined && e.button !== 0) return;
      el.classList.add('is-dragging');
      try { el.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
      splitFromPointer(e);
    });
    el.addEventListener('pointermove', function (e) {
      if (!el.classList.contains('is-dragging')) return;
      splitFromPointer(e);
    });
    ['pointerup', 'pointercancel'].forEach(function (ev) {
      el.addEventListener(ev, function () { el.classList.remove('is-dragging'); });
    });

    if (handle) {
      handle.addEventListener('keydown', function (e) {
        var step = e.shiftKey ? 10 : 4;
        var next = null;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = split - step;
        else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = split + step;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = 100;
        if (next === null) return;
        e.preventDefault();
        setSplit(next);
      });
    }

    setSplit(start);
  }

  /* ------------------------------------------------------------------ *
   * 5. Order intake — uploads + AJAX add-to-cart + drawer
   * ------------------------------------------------------------------ */
  var ALLOWED_EXT = /\.(jpe?g|png|webp|heic|heif)$/i;
  var ALLOWED_TYPE = /^image\/(jpe?g|png|webp|heic|heif)$/i;
  var MAX_ORIGINAL_BYTES = 20 * 1024 * 1024; // 20MB
  var RAW_FALLBACK_MAX_BYTES = 4 * 1024 * 1024; // middleware body ceiling
  var DOWNSCALE_MAX_EDGE = 2000;
  var DOWNSCALE_QUALITY = 0.85;
  var STORY_MIN = 10;
  var STORY_MAX = 1500;
  var HEADLINE_MAX = 40; // short & punchy — 4-5 words max

  function initOrder(root) {
    if (root.dataset.pgReady === '1') return;
    root.dataset.pgReady = '1';

    var cfg = {
      middlewareUrl: (root.dataset.middlewareUrl || '').replace(/\/+$/, ''),
      variantId: parseInt(root.dataset.variantId, 10) || 0,
      maxPhotos: parseInt(root.dataset.maxPhotos, 10) || 6
    };

    var q = function (sel) { return root.querySelector('[data-pg="' + sel + '"]'); };

    var form = root.querySelector('[data-pg-order-form]');
    var storyEl = q('story');
    var ptypeBoxes = root.querySelectorAll('[data-pg="ptype"]');
    var headlineSel = q('headline');
    var headlineText = q('headline-text');
    var headlineCounter = q('headline-counter');
    var headlineCount = q('headline-count');
    function syncHeadlineCount() {
      if (!headlineText) return;
      // Hard cap at HEADLINE_MAX even if the maxlength attribute is bypassed.
      if ((headlineText.value || '').length > HEADLINE_MAX) {
        headlineText.value = headlineText.value.slice(0, HEADLINE_MAX);
      }
      if (headlineCount) headlineCount.textContent = String((headlineText.value || '').length);
    }
    // "Custom Headline (typed)" reveals the free-text input; disabled inputs
    // never submit an empty property (mirrors blocks/_custom-design-intake).
    if (headlineSel && headlineText) {
      headlineSel.addEventListener('change', function () {
        var custom = /custom/i.test(headlineSel.value || '');
        headlineText.hidden = !custom;
        headlineText.disabled = !custom;
        if (!custom) headlineText.value = '';
        if (headlineCounter) headlineCounter.hidden = !custom;
        syncHeadlineCount();
      });
      headlineText.addEventListener('input', syncHeadlineCount);
    }
    function selectedTypes() {
      var out = [];
      ptypeBoxes.forEach(function (b) { if (b.checked) out.push(b.value); });
      return out;
    }
    var storyCount = q('story-count');
    var drop = q('drop');
    var fileInput = q('file-input');
    var thumbs = q('thumbs');
    var statusBox = q('status');
    var statusMsg = q('status-msg');
    var bar = q('bar');
    var errorsBox = q('errors');
    var successBox = q('success');
    var submitBtn = q('submit');
    var submitLabel = submitBtn ? submitBtn.textContent : '';

    var photos = []; // { id, file, previewUrl, status: 'uploading'|'done'|'error', url, error }
    var nextId = 1;

    /* ---------- photos: eager upload to the middleware ---------- */

    function fileAllowed(file) {
      return ALLOWED_TYPE.test(file.type || '') || ALLOWED_EXT.test(file.name || '');
    }

    function addFiles(fileList) {
      var rejected = [];
      Array.prototype.forEach.call(fileList, function (file) {
        if (photos.length >= cfg.maxPhotos) {
          rejected.push(file.name + ' — limit is ' + cfg.maxPhotos + ' photos');
          return;
        }
        if (!fileAllowed(file)) {
          rejected.push(file.name + ' — must be JPG, PNG, HEIC or WEBP');
          return;
        }
        if (file.size > MAX_ORIGINAL_BYTES) {
          rejected.push(file.name + ' — over 20MB');
          return;
        }
        var p = {
          id: nextId++,
          file: file,
          previewUrl: URL.createObjectURL(file),
          status: 'uploading',
          url: null,
          error: null
        };
        photos.push(p);
        processPhoto(p);
      });
      if (rejected.length) showErrors(rejected); else clearErrors();
      renderThumbs();
      syncStatus();
    }

    function processPhoto(p) {
      downscale(p.file)
        .then(function (prepared) {
          if (!prepared) {
            throw new Error('could not be processed in this browser — re-save it as JPG or PNG');
          }
          return uploadOne(prepared, p.file.name || ('photo-' + p.id));
        })
        .then(function (url) {
          p.status = 'done';
          p.url = url;
        })
        .catch(function (err) {
          p.status = 'error';
          p.error = err && err.message ? err.message : 'upload failed';
        })
        .then(function () {
          renderThumbs();
          syncStatus();
        });
    }

    function removePhoto(id) {
      for (var i = 0; i < photos.length; i++) {
        if (photos[i].id === id) {
          try { URL.revokeObjectURL(photos[i].previewUrl); } catch (e) { /* noop */ }
          photos.splice(i, 1);
          break;
        }
      }
      renderThumbs();
      syncStatus();
    }

    function renderThumbs() {
      if (!thumbs) return;
      thumbs.textContent = '';
      photos.forEach(function (p) {
        var cell = document.createElement('div');
        cell.className = 'lig-pg-thumb' + (p.status === 'error' ? ' is-error' : '');

        var img = document.createElement('img');
        img.src = p.previewUrl;
        img.alt = 'Photo of your dog, ready to upload';
        cell.appendChild(img);

        var del = document.createElement('button');
        del.type = 'button';
        del.className = 'lig-pg-thumb__del';
        del.setAttribute('aria-label', 'Remove photo');
        del.textContent = '×';
        del.addEventListener('click', function () { removePhoto(p.id); });
        cell.appendChild(del);

        var state = document.createElement('span');
        state.className = 'lig-pg-thumb__state';
        state.textContent =
          p.status === 'uploading' ? 'uploading…' :
          p.status === 'done' ? 'uploaded' :
          (p.error || 'failed');
        cell.appendChild(state);

        thumbs.appendChild(cell);
      });
    }

    function doneUrls() {
      return photos
        .filter(function (p) { return p.status === 'done' && p.url; })
        .map(function (p) { return p.url; });
    }

    function syncStatus() {
      if (!statusBox) return;
      var done = doneUrls().length;
      var uploading = photos.some(function (p) { return p.status === 'uploading'; });
      var errored = photos.filter(function (p) { return p.status === 'error'; }).length;
      if (uploading) {
        setStatus('Uploading… (' + done + '/' + photos.length + ')',
          photos.length ? (done / photos.length) * 100 : 0);
      } else if (errored > 0) {
        setStatus(errored + ' photo' + (errored > 1 ? 's' : '') + ' failed — remove and re-add, or just email them after checkout.', 100, true);
      } else if (photos.length > 0) {
        setStatus('All ' + done + ' photo' + (done > 1 ? 's' : '') + ' uploaded.', 100);
      } else {
        statusBox.hidden = true;
      }
    }

    function downscale(file) {
      return new Promise(function (resolve) {
        var url = URL.createObjectURL(file);
        var img = new Image();
        img.onload = function () {
          URL.revokeObjectURL(url);
          try {
            var w = img.naturalWidth || img.width;
            var h = img.naturalHeight || img.height;
            var scale = Math.min(1, DOWNSCALE_MAX_EDGE / Math.max(w, h));
            var cw = Math.max(1, Math.round(w * scale));
            var ch = Math.max(1, Math.round(h * scale));
            var canvas = document.createElement('canvas');
            canvas.width = cw;
            canvas.height = ch;
            canvas.getContext('2d').drawImage(img, 0, 0, cw, ch);
            canvas.toBlob(function (blob) {
              resolve(blob ? { blob: blob, contentType: 'image/jpeg', ext: 'jpg' } : rawFallback(file));
            }, 'image/jpeg', DOWNSCALE_QUALITY);
          } catch (e) {
            resolve(rawFallback(file));
          }
        };
        img.onerror = function () {
          URL.revokeObjectURL(url);
          resolve(rawFallback(file)); // e.g. HEIC outside Safari
        };
        img.src = url;
      });
    }

    function rawFallback(file) {
      if (file.size > RAW_FALLBACK_MAX_BYTES) return null;
      var ct = file.type && ALLOWED_TYPE.test(file.type) ? file.type : 'image/jpeg';
      var ext = (file.name.match(ALLOWED_EXT) || ['', 'jpg'])[1] || 'jpg';
      return { blob: file, contentType: ct, ext: ext.toLowerCase() };
    }

    function uploadOne(prepared, baseName) {
      if (!cfg.middlewareUrl) {
        return Promise.reject(new Error('uploads not configured — email your photos after checkout instead'));
      }
      var fname = baseName.replace(/\.[^.]+$/, '') + '.' + prepared.ext;
      var endpoint = cfg.middlewareUrl + '/api/custom-design/upload?filename=' + encodeURIComponent(fname);
      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': prepared.contentType },
        body: prepared.blob
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          if (!res.ok || !data.url) {
            throw new Error(data.error || ('upload failed (' + res.status + ')'));
          }
          return data.url;
        });
      });
    }

    /* ---------- validation (inline; never a native dialog) ---------- */

    function validate() {
      var errs = [];
      if (!selectedTypes().length) errs.push('Pick at least one product type — what should we put the design on?');
      var story = (storyEl && storyEl.value || '').trim();
      if (story.length < STORY_MIN) {
        errs.push('Tell us your vision — at least ' + STORY_MIN + ' characters.');
      } else if (story.length > STORY_MAX) {
        errs.push('Your vision is over the ' + STORY_MAX + '-character limit.');
      }
      if (photos.some(function (p) { return p.status === 'uploading'; })) {
        errs.push('One sec — your photos are still uploading, then try again.');
      } else if (photos.some(function (p) { return p.status === 'error'; })) {
        errs.push('A photo failed to upload — remove it and try another.');
      } else if (!doneUrls().length) {
        errs.push('Add at least one photo of your dog — we draw their likeness from it.');
      }
      if (headlineSel && /custom/i.test(headlineSel.value || '')) {
        var hl = (headlineText && headlineText.value || '').trim();
        if (!hl) {
          errs.push('Enter your custom headline text — or keep the standard Life Is Golden branding.');
        } else if (hl.length > HEADLINE_MAX) {
          errs.push('Keep your custom headline under ' + HEADLINE_MAX + ' characters — 4–5 words max.');
        }
      }
      return errs;
    }

    /* ---------- cart pipeline (mirrors lig-bundle-builder) ---------- */

    function cartSectionIds() {
      var ids = [];
      document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (node) {
        var id = node.getAttribute('data-section-id');
        if (id && ids.indexOf(id) === -1) ids.push(id);
      });
      return ids.join(',');
    }

    function openCartDrawer() {
      var drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') {
        try { drawer.open(); } catch (e) { /* a closed/absent drawer never breaks the add */ }
      }
    }

    function dispatchCartUpdate(cart, sections) {
      // Vanilla twin of @theme/events CartAddEvent: a bubbling `cart:update`
      // Event with a manually-assigned `detail` (CartAddEvent extends Event and
      // assigns detail the same way, so listeners can't tell the difference).
      var ev;
      try {
        ev = new Event('cart:update', { bubbles: true });
      } catch (e) {
        return;
      }
      ev.detail = {
        resource: cart,
        sourceId: 'lig-pg-order',
        data: {
          source: 'lig-pg-order',
          itemCount: cart && typeof cart.item_count === 'number' ? cart.item_count : undefined,
          variantId: cfg.variantId,
          sections: sections
        }
      };
      document.dispatchEvent(ev);
    }

    function addToCart() {
      var routes = themeRoutes();
      var addUrl = routes.cart_add_url || '/cart/add.js';
      var cartJsUrl = (routes.cart_url || '/cart') + '.js';

      var vision = (storyEl.value || '').trim();
      var properties = {
        '_design_product_types': selectedTypes().join(', '),
        '_design_vision': vision,
        '_design_headline_pref': headlineSel ? headlineSel.value : ''
      };
      if (headlineText && !headlineText.disabled && (headlineText.value || '').trim()) {
        properties['_design_headline_text'] = headlineText.value.trim();
      }
      var urls = doneUrls();
      properties['_design_photo_urls'] = urls.join('\n');
      properties['_design_photo_count'] = String(urls.length);

      setBusy(true);

      var payload = { items: [{ id: cfg.variantId, quantity: 1, properties: properties }] };
      var sectionIds = cartSectionIds();
      if (sectionIds) {
        payload.sections = sectionIds;
        payload.sections_url = window.location.pathname;
      }

      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          return res.text().then(function (text) {
            var parsed;
            try { parsed = JSON.parse(text); } catch (e) { throw new Error('Unexpected cart response'); }
            if (!res.ok || parsed.status) {
              throw new Error(parsed.description || parsed.message || 'Could not add the design setup to your cart.');
            }
            return parsed;
          });
        })
        .then(function (parsed) {
          return fetch(cartJsUrl, { headers: { Accept: 'application/json' } })
            .then(function (res) { return res.json(); })
            .catch(function () { return null; })
            .then(function (cart) {
              dispatchCartUpdate(cart || parsed, parsed.sections);
              openCartDrawer();
              showSuccess();
            });
        })
        .catch(function (err) {
          showErrors([err && err.message ? err.message : 'Could not add to cart — please try again.']);
        })
        .then(function () {
          setBusy(false);
        });
    }

    /* ---------- inline UI helpers ---------- */

    function setBusy(busy) {
      if (!submitBtn) return;
      if (busy) {
        submitBtn.setAttribute('aria-busy', 'true');
        submitBtn.disabled = true;
        submitBtn.textContent = 'Adding…';
      } else {
        submitBtn.removeAttribute('aria-busy');
        submitBtn.disabled = false;
        submitBtn.textContent = submitLabel;
      }
    }

    function showErrors(list) {
      if (!errorsBox) return;
      if (successBox) successBox.hidden = true;
      errorsBox.textContent = '';
      var strong = document.createElement('strong');
      strong.textContent = 'Almost there:';
      errorsBox.appendChild(strong);
      var ul = document.createElement('ul');
      list.forEach(function (msg) {
        var li = document.createElement('li');
        li.textContent = String(msg);
        ul.appendChild(li);
      });
      errorsBox.appendChild(ul);
      errorsBox.hidden = false;
    }

    function clearErrors() {
      if (!errorsBox) return;
      errorsBox.hidden = true;
      errorsBox.textContent = '';
    }

    function showSuccess() {
      clearErrors();
      if (successBox) successBox.hidden = false;
      // Reset the brief so a stray second click can't add a second setup fee.
      if (storyEl) storyEl.value = '';
      if (storyCount) storyCount.textContent = '0';
      if (headlineText) headlineText.value = '';
      if (headlineCount) headlineCount.textContent = '0';
      photos.forEach(function (p) {
        try { URL.revokeObjectURL(p.previewUrl); } catch (e) { /* noop */ }
      });
      photos = [];
      renderThumbs();
      syncStatus();
    }

    function setStatus(msg, pct, isError) {
      if (!statusBox) return;
      statusBox.hidden = false;
      if (statusMsg) {
        statusMsg.textContent = msg;
        statusMsg.style.color = isError ? '#8f2f22' : '';
      }
      if (bar && typeof pct === 'number') {
        bar.style.width = Math.max(0, Math.min(100, pct)) + '%';
      }
    }

    /* ---------- wiring ---------- */

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var errs = validate();
        if (errs.length) {
          showErrors(errs);
          if (errorsBox && errorsBox.scrollIntoView) {
            errorsBox.scrollIntoView({ behavior: prefersReduced() ? 'auto' : 'smooth', block: 'nearest' });
          }
          return;
        }
        clearErrors();
        addToCart();
      });
    }

    if (drop && fileInput) {
      drop.addEventListener('click', function () { fileInput.click(); });
      drop.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.click(); }
      });
      fileInput.addEventListener('change', function () {
        if (fileInput.files && fileInput.files.length) addFiles(fileInput.files);
        fileInput.value = '';
      });
      ['dragenter', 'dragover'].forEach(function (ev) {
        drop.addEventListener(ev, function (e) {
          e.preventDefault();
          e.stopPropagation();
          drop.classList.add('is-hover');
        });
      });
      ['dragleave', 'drop'].forEach(function (ev) {
        drop.addEventListener(ev, function (e) {
          e.preventDefault();
          e.stopPropagation();
          drop.classList.remove('is-hover');
        });
      });
      drop.addEventListener('drop', function (e) {
        var dt = e.dataTransfer;
        if (dt && dt.files && dt.files.length) addFiles(dt.files);
      });
    }

    if (storyEl && storyCount) {
      var updateCount = function () {
        storyCount.textContent = String((storyEl.value || '').length);
      };
      storyEl.addEventListener('input', updateCount);
      updateCount();
    }
  }

  /* ------------------------------------------------------------------ *
   * Boot + theme-editor re-init
   * ------------------------------------------------------------------ */
  function boot(scope) {
    var ctx = scope || document;
    if (!ctx.querySelector('.lig-pg') && ctx !== document) return;
    if (!prefersReduced()) document.documentElement.classList.add('pg-js');
    initAnchors();
    initReveals(ctx);
    ctx.querySelectorAll('[data-pg-story]').forEach(initStory);
    ctx.querySelectorAll('[data-pg-compare]').forEach(initCompare);
    ctx.querySelectorAll('[data-pg-order]').forEach(initOrder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelectorAll) boot(e.target);
  });
})();
